import { FC, useCallback, useMemo, useState } from "react";
import { PlainModal } from "@design-system/Modal/Modal";
import { UpdateRuleGroupModal } from "@components/settings/Rules/RuleGroup/UpdateRuleGroupModal";
import { DeleteRuleGroupModal } from "@components/settings/Rules/RuleGroup/DeleteRuleGroupModal";
import { CreateRuleGroupModal } from "@components/settings/Rules/RuleGroup/CreateRuleGroupModal";
import {
    RuleGroupType,
    TransactionRuleGroup,
} from "@common/types/transactionRule";
import { ChildrenProps } from "../../../types";
import { TransactionRuleManagementContext } from "./TransactionRuleManagement.context";

export const TransactionRuleManagement: FC<ChildrenProps> = ({ children }) => {
    const [ruleGroupToUpdate, setRuleGroupToUpdate] =
        useState<TransactionRuleGroup | null>(null);
    const [ruleGroupToDelete, setRuleGroupToDelete] =
        useState<TransactionRuleGroup | null>(null);
    const [isCreatingRuleGroup, setIsCreatingRuleGroup] =
        useState<RuleGroupType | null>(null);

    const updateRuleGroup = useCallback(
        (ruleGroup: TransactionRuleGroup | null) => {
            setRuleGroupToUpdate(ruleGroup);
        },
        [],
    );

    const deleteRuleGroup = useCallback(
        (ruleGroup: TransactionRuleGroup | null) => {
            setRuleGroupToDelete(ruleGroup);
        },
        [],
    );

    const createRuleGroup = useCallback((type: RuleGroupType) => {
        setIsCreatingRuleGroup(type);
    }, []);

    const contextValue = useMemo(
        () => ({
            updateRuleGroup,
            deleteRuleGroup,
            createRuleGroup,
            ruleGroupToUpdate,
            ruleGroupToDelete,
            isCreatingRuleGroup,
        }),
        [
            updateRuleGroup,
            deleteRuleGroup,
            createRuleGroup,
            ruleGroupToUpdate,
            ruleGroupToDelete,
            isCreatingRuleGroup,
        ],
    );

    return (
        <>
            <TransactionRuleManagementContext.Provider value={contextValue}>
                {children}
            </TransactionRuleManagementContext.Provider>

            <PlainModal
                show={!!ruleGroupToUpdate}
                onHide={() => {
                    updateRuleGroup(null);
                    deleteRuleGroup(null);
                }}
            >
                <UpdateRuleGroupModal
                    ruleGroup={ruleGroupToUpdate}
                    onHide={() => setRuleGroupToUpdate(null)}
                />
            </PlainModal>
            {ruleGroupToDelete && (
                <DeleteRuleGroupModal
                    ruleGroup={ruleGroupToDelete}
                    onHide={() => setRuleGroupToDelete(null)}
                />
            )}
            {isCreatingRuleGroup !== null && (
                <PlainModal show onHide={() => setIsCreatingRuleGroup(null)}>
                    <CreateRuleGroupModal
                        onHide={() => setIsCreatingRuleGroup(null)}
                        type={isCreatingRuleGroup}
                    />
                </PlainModal>
            )}
        </>
    );
};
